import { ethers } from 'ethers'
import { Contract } from '@ethersproject/contracts'
import type { MarketplaceOffer } from '@mprolab/sdk'
import {
  MARKETPLACE_ABI,
  MULTICALL3_ABI,
  MULTICALL3_ADDRESS
} from './abi'

/**
 * Every buy offer, read straight from the contract — the mirror of
 * readAllListings, and tolerant of the pre-upgrade world: on V1 the
 * `nextOfferId()` selector does not exist, the probe call fails, and this
 * returns an empty list instead of an error. The marketplace simply shows
 * no Wanted cards until the upgrade lands.
 */
export async function readAllOffers(params: {
  marketplaceAddress: string
  provider: ethers.providers.Provider
}): Promise<MarketplaceOffer[]> {
  const market = new Contract(
    params.marketplaceAddress,
    MARKETPLACE_ABI as unknown as ethers.ContractInterface,
    params.provider
  )

  let lastId: number
  try {
    const next: ethers.BigNumber = await market.nextOfferId()
    lastId = next.toNumber()
  } catch {
    // V1: offers do not exist yet.
    return []
  }
  if (lastId < 1) return []

  const ids = Array.from({ length: lastId }, (_, i) => i + 1)
  const calls = ids.map((id) => ({
    target: params.marketplaceAddress,
    allowFailure: true,
    callData: market.interface.encodeFunctionData('offers', [id])
  }))
  const multicall = new Contract(
    MULTICALL3_ADDRESS,
    MULTICALL3_ABI as unknown as ethers.ContractInterface,
    params.provider
  )
  const raw: Array<{ success: boolean; returnData: string }> =
    await multicall.callStatic.aggregate3(calls)

  const out: MarketplaceOffer[] = []
  for (let i = 0; i < raw.length; i++) {
    if (!raw[i].success) continue
    try {
      const d = market.interface.decodeFunctionResult('offers', raw[i].returnData)
      out.push({
        offerId: String(ids[i]),
        buyer: String(d.buyer).toLowerCase(),
        nft: String(d.nft).toLowerCase(),
        unitPriceWei: d.unitPrice.toString(),
        remaining: d.remaining.toString(),
        createdAt: Number(d.createdAt.toString()),
        expiresAt: Number(d.expiresAt.toString()),
        active: Boolean(d.active)
        , feeBps: Number(d.feeBps)
      })
    } catch {
      // Skip undecodable rows.
    }
  }
  return out
}

/** Single-offer read — the detail page and the accept flow. */
export async function readOffer(params: {
  marketplaceAddress: string
  provider: ethers.providers.Provider
  offerId: string
}): Promise<MarketplaceOffer | null> {
  const market = new Contract(
    params.marketplaceAddress,
    MARKETPLACE_ABI as unknown as ethers.ContractInterface,
    params.provider
  )
  try {
    const d = await market.offers(params.offerId)
    if (!d || d.buyer === ethers.constants.AddressZero) return null
    return {
      offerId: String(params.offerId),
      buyer: String(d.buyer).toLowerCase(),
      nft: String(d.nft).toLowerCase(),
      unitPriceWei: d.unitPrice.toString(),
      remaining: d.remaining.toString(),
      createdAt: Number(d.createdAt.toString()),
      expiresAt: Number(d.expiresAt.toString()),
      active: Boolean(d.active),
      feeBps: Number(d.feeBps)
    }
  } catch {
    return null
  }
}

/** True when a non-zero expiry has passed — mirrors the contract's rule. */
export const isExpired = (expiresAt?: number): boolean =>
  Boolean(expiresAt && expiresAt * 1000 <= Date.now())
